import React from "react";
import { useState, useEffect } from 'react';
import { ListGroup, Row, Col, Form } from "react-bootstrap"
import { SelectableListItem } from "./SelectableListItem";
import { ISelectableListItem } from "../../interfaces/Components/SelectableList/ISelectableListItem"
import { ISelectableListProps } from '../../interfaces/Components/SelectableList/ISelectableListProps';
import { SelectableListSearch } from "./SelectableListSearch";
import { ListPagination } from "../ListPagination/ListPagination";
import { PaginationChangeType } from "../../interfaces/Components/ListPagination/IListPagingProps"; 
import { NoData } from "../NoData/NoData";

const SelectableList = ({items, onChange, options, uniqueName, label} : ISelectableListProps) => {

    const [allItems, setAllItems] = useState(new Array<ISelectableListItem>());
    const [filteredItems, setFilteredItems] = useState(items);
    const [visibleCount, setVisibleCount] = useState(items.length);
    const [selectedCount, setSelectedCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const resultsPerPage = (options?.paginationOptions?.resultsPerPage) ? options?.paginationOptions?.resultsPerPage : 5;
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [filterText, setFilterText] = useState("");

    const noResultsMessage = (!options?.noResultsMessage) ?  "No results" : options?.noResultsMessage;

    useEffect(() => {
        setAllItems(items);
    },[items]);

    useEffect(() => {
        const newList = allItems.slice();

        let filteredList : Array<ISelectableListItem> = newList.filter(item => item.Label.toLowerCase().indexOf(filterText) > -1);
        const nonPagedFilterCount = filteredList.length;

        setVisibleCount(filteredList.length);
        setSelectedCount(filteredList.filter(item => item.Checked).length);

        if(options?.showPagination) {
            const start = (currentPage - 1) * resultsPerPage;
            const end = start + resultsPerPage;
            filteredList = filteredList.slice(start, end);
        }

        let numberOfPages = Math.ceil(nonPagedFilterCount / resultsPerPage);
        if(numberOfPages === 0) {
            numberOfPages = 1;
        }

        setNumberOfPages(numberOfPages);
        
        if(currentPage > numberOfPages) {
            setCurrentPage(numberOfPages);
        }
        
        setFilteredItems(filteredList);
    }, [allItems, filterText, numberOfPages, options?.showPagination, resultsPerPage, currentPage]);

    useEffect(() => {
        const newList = allItems.slice();
        let filteredList : Array<ISelectableListItem> = newList.filter(item => item.Label.toLowerCase().indexOf(filterText) > -1);

        if(options?.showPagination)
        {
            const start = (currentPage - 1) * resultsPerPage;
            const end = start + resultsPerPage;
            filteredList = filteredList.slice(start, end);
        }

        setFilteredItems(filteredList);

    }, [currentPage, allItems, filterText, options?.showPagination, resultsPerPage]);

    const itemChangedHandler = (key: string, checkValue: boolean) => {

        const newAllItems = allItems.slice();
        const filteredListKeys : Array<string> = newAllItems.filter(item => item.Key === key).map(item => item.Key);
        newAllItems.forEach(item => {
            if(filteredListKeys.includes(item.Key)) {
                item.Checked = checkValue;
            }
        });
        setAllItems(newAllItems);
        onChange(newAllItems);
    };

    const searchChangedHandler = (value : string) => {

        const lowerValue = value.toLowerCase();
        setFilterText(lowerValue);
    };

    const selectAllChangedHandler = () => {
        const currentlyCheckedList : Array<ISelectableListItem> = filteredItems.filter(item => item.Checked === true);
        const settingToChecked = (currentlyCheckedList.length < filteredItems.length);
        const filteredListKeys = filteredItems.map(item => item.Key);
       
        const newAllItems = allItems.slice();
        newAllItems.forEach(item => {
            if(filteredListKeys.includes(item.Key)) {
                item.Checked = settingToChecked;
            }
        });
        setAllItems(newAllItems);
        onChange(newAllItems);
    };

    const pageChangeHandler = (type: PaginationChangeType, key : number) => {
        switch(+type)
        {
            case PaginationChangeType.Back:
                if(currentPage - 1 > 0) {
                    setCurrentPage(currentPage - 1);
                }
                break;
            case PaginationChangeType.Forward:
                if(currentPage + 1 <= numberOfPages) {
                    setCurrentPage(currentPage + 1);
                }
                break;
            case PaginationChangeType.Direct:
                setCurrentPage(key);
                break;
        }
    }

    const renderSelectAll = () => {

        const ariaLabel : string = (label) ? `Select all for ${label}` : "Select all";

        return <Form.Group className="mb-1">
                   <Form.Check type="checkbox" aria-label={ariaLabel} onClick={selectAllChangedHandler} readOnly checked={visibleCount === selectedCount && selectedCount !== 0}/>
               </Form.Group>
    };

    const renderSelectCount = () => {

        const accessibilityText = (label) ? <span className="visually-hidden">of {label}</span> : null;

        return <p id={`${uniqueName}-selectable-list-count-holder`} className="mb-1">{selectedCount}/{visibleCount} selected {accessibilityText}</p>
    };

    const renderHeader = () => {
        if(!(options?.showSearch || options?.showSelectAll || options?.showSelectedCount)) {
            return;
        }

        const showHeaderRow = options?.showSelectAll || options?.showSelectedCount;

        return <ListGroup.Item>
                    {showHeaderRow && (<Row>
                        {options?.showSelectAll && <Col xs={'auto'} className="pr-0">
                            {renderSelectAll()}
                        </Col>}
                        {options?.showSelectedCount && <Col className="pl-1">
                            {renderSelectCount()}
                        </Col>}
                    </Row>)}
                    {options?.showSearch && <Row>
                        <Col>
                            {options?.showSearch && <SelectableListSearch uniqueName={uniqueName} label={`Search ${label}`} onChange={searchChangedHandler}></SelectableListSearch>}
                        </Col>
                    </Row>}
               </ListGroup.Item>
    };

    const renderItems = () => {
        let items : Array<JSX.Element> = [];
        filteredItems.forEach((item : ISelectableListItem) => {
            items.push(<SelectableListItem uniqueName={uniqueName} key={item.Key} item={item} onChange={itemChangedHandler}></SelectableListItem>)
        });
        if (items.length === 0) {
            items.push(<ListGroup.Item key="noData"><NoData visible={true} description={noResultsMessage} /></ListGroup.Item>);
        }
        else if(items.length > 0 && items.length < resultsPerPage) {
            const missingCount = resultsPerPage - items.length;
            for(let i = 0; i < missingCount; i+=1) { 
                items.push(<ListGroup.Item key={`empty${i}`}>&nbsp;</ListGroup.Item>);
            }
        }

        return <>{items}</>
    };

    const renderFooter = () => {
        if(!options?.showPagination) {
            return;
        }

        return  <>
                    <ListGroup.Item>
                        <ListPagination 
                            activePage={currentPage}
                            numberOfPages={numberOfPages}
                            onChange={pageChangeHandler}
                            showPrevNextMarkers={options?.paginationOptions?.showPrevNextMarkers}
                            maxPageMarkersToShow={options?.paginationOptions?.maxPageMarkersToShow}
                            accessibilityPrefix="page"
                            accessibilitySuffix={(label) ? `of ${label}` : ""} />
                    </ListGroup.Item>
                </>
    };

    return <ListGroup className="selectable-list">
        {renderHeader()}
        {renderItems()}
        {renderFooter()}
    </ListGroup>
};

export { SelectableList };